// @flow

import { get } from 'lodash/fp';

import * as challengesApi from '../api/challenges';
import {
  API_CALL,
  RESET_MY_TEAMS,
  SET_LINEUP_TYPE_FILTER,
  SET_SELECTED_LINEUP_IDS,
  START_API_POLL,
  STOP_API_POLL,
  SUBMIT_MULTIPLE_LINEUP_ENTRY_REQUEST,
  SUBMIT_MULTIPLE_LINEUP_ENTRY_SUCCESS,
  SUBMIT_MULTIPLE_LINEUP_ENTRY_FAILURE,
  SWITCH_LINEUP_TYPE_FILTER
} from '../constants/actionTypes';
import {
  CLEAR_FAVORITES,
  DELETE_LINEUPS,
  MY_LINEUP,
  MY_LINEUPS,
  MY_LINEUP_CHALLENGES,
  MY_SLATE_LINEUPS,
  TOGGLE_LINEUP_FAVORITE,
  UPDATE_MY_LINEUP
} from '../constants/apiTypes';
import { LIVE_TEAM_POLL_DURATION } from '../constants/pollDurations';
import { ACTIVE, RUNNING } from '../constants/lineupStatus';
import selectionToPost from '../selectors/selectionToPost';
import selectionByLineup from '../selectors/selectionByLineup';

export function clearFavorites() {
  return {
    type: API_CALL,
    api: CLEAR_FAVORITES,
    request: challengesApi.clearFavorites()
  };
}

export function deleteLineups(query: Object) {
  return {
    type: API_CALL,
    api: DELETE_LINEUPS,
    request: challengesApi.deleteLineups(query)
  };
}

export function getLineupChallenges(lineupId, query: Object) {
  return {
    type: API_CALL,
    api: MY_LINEUP_CHALLENGES,
    request: challengesApi.getLineupChallenges(lineupId, query)
  };
}

export function getMyLineUps(query: Object = { status: ACTIVE }) {
  return {
    type: API_CALL,
    api: MY_LINEUPS,
    request: challengesApi.getLineUps(query)
  };
}

export function getMyLineUp(id: number) {
  return {
    type: API_CALL,
    api: MY_LINEUP,
    request: challengesApi.getLineUp(id)
  };
}

export function setSelectedLineupIds(lineupIds: Array<number>) {
  return {
    type: SET_SELECTED_LINEUP_IDS,
    lineupIds
  };
}

export function toggleLineupFavorite(id: number, favorite: boolean) {
  return {
    type: API_CALL,
    api: TOGGLE_LINEUP_FAVORITE,
    id,
    favorite,
    request: challengesApi.toggleLineupFavorite(id, favorite)
  };
}

export function updateMyLineup(id, data) {
  return (dispatch: Dispatch<*>, getState: GetState) => {
    const state = getState();
    const finalData = {
      ...data,
      selection: selectionToPost(state)
    };

    dispatch({
      type: API_CALL,
      api: UPDATE_MY_LINEUP,
      request: challengesApi.updateLineup(id, finalData)
    });
  };
}

export function getSlateLineUps(query) {
  return {
    type: API_CALL,
    api: MY_SLATE_LINEUPS,
    request: challengesApi.getSlateLineUps(query)
  };
}

export function resetMyTeams() {
  return {
    type: RESET_MY_TEAMS
  };
}

export function setLineupTypeFilter(value) {
  return {
    type: SET_LINEUP_TYPE_FILTER,
    value
  };
}

export function switchLineupTypeFilter(value) {
  return {
    type: SWITCH_LINEUP_TYPE_FILTER,
    value
  };
}

export function stopPollingMyLiveLineUps() {
  return {
    type: STOP_API_POLL,
    key: MY_LINEUPS
  };
}

export function pollMyLiveLineUps(query: Object = { status: RUNNING }) {
  return {
    type: START_API_POLL,
    key: MY_LINEUPS,
    duration: LIVE_TEAM_POLL_DURATION,
    getApiCall: () => ({
      api: MY_LINEUPS,
      request: () => challengesApi.getLineUps(query)
    })
  };
}

export function submitMultiplePxcEntries(data = {}) {
  return (dispatch: Dispatch<*>, getState: GetState) => {
    const state = getState();
    const {
      challenge: { cost, id: challengeId },
      myTeams: { selectedLineupIds }
    } = state;

    dispatch({
      type: SUBMIT_MULTIPLE_LINEUP_ENTRY_REQUEST
    });

    const totalCost = selectedLineupIds.length * cost;

    Promise.all(
      selectedLineupIds.map(lineupId =>
        challengesApi
          .submitPxcEntries({
            selection: selectionByLineup(state, lineupId),
            challenges: [
              {
                id: challengeId,
                quantity: 1
              }
            ],
            transfer_id: data.batchId
          })
          .then(payload => ({
            ...payload,
            challengeId,
            errors: get('meta.soft_errors', payload)
          }))
          .catch(e => ({ errors: get('errors', e) }))
      )
    ).then(
      payload => {
        dispatch({
          type: SUBMIT_MULTIPLE_LINEUP_ENTRY_SUCCESS,
          payload,
          totalCost
        });
      },
      payload => {
        dispatch({
          type: SUBMIT_MULTIPLE_LINEUP_ENTRY_FAILURE,
          payload
        });
      }
    );
  };
}

export function submitMultipleEntries() {
  return (dispatch: Dispatch<*>, getState: GetState) => {
    const state = getState();
    const {
      challenge: { cost, id: challengeId },
      myTeams: { selectedLineupIds }
    } = state;

    dispatch({
      type: SUBMIT_MULTIPLE_LINEUP_ENTRY_REQUEST
    });

    const totalCost = selectedLineupIds.length * cost;

    Promise.all(
      selectedLineupIds.map((lineupId, i) =>
        new Promise(resolve => setTimeout(resolve, i * 1000)).then(() =>
          challengesApi
            .submitMultipleEntries(challengeId, {
              selection: selectionByLineup(state, lineupId),
              quantity: 1
            })
            .then(payload => ({
              ...payload,
              challengeId,
              errors: get('meta.soft_errors', payload)
            }))
            .catch(e => ({ errors: get('errors', e) }))
        )
      )
    ).then(
      payload => {
        dispatch({
          type: SUBMIT_MULTIPLE_LINEUP_ENTRY_SUCCESS,
          payload,
          totalCost
        });
      },
      payload => {
        dispatch({
          type: SUBMIT_MULTIPLE_LINEUP_ENTRY_FAILURE,
          payload
        });
      }
    );
  };
}
